import { makeKindKeyStringFromKindKey } from '@gonfalon/contexts/kind-key/makeKindKeyString';
import { ContextKindKey } from '@gonfalon/contexts/types';
import { enableClickhouseContextsApiChangesInTheUi } from '@gonfalon/dogfood-flags/dogfood-flags';
import { http, middleware } from '@gonfalon/legacy-http-client/httpUtils';
import type { schemas } from '@gonfalon/openapi/types';
import type { Queryfilter as QueryFilter } from '@gonfalon/queryfilter/builder';
import {
  and,
  anyOf,
  equals,
  exists,
  or,
  parenthesized,
  toString as queryfilterToString,
  startsWith,
} from '@gonfalon/queryfilter/builder';
import { batchedContextsSearchLimit, contextsSearchSort } from '@gonfalon/rest-api/migrations/contexts';
import type { ContextKindsCollectionRep } from '@gonfalon/rest-api/types';

import type { ContextTargetKindKey } from '../types';
import { augmentResolveContextsResponse } from '../utils/contextTargetingUtils';

export const fetchContextList = (projectKey: string, environmentKey: string, kind: string, prefix: string) => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/contexts/search`,
    window.location.origin,
  );
  const searchParams = new URLSearchParams();
  const matches: QueryFilter[] = [];
  matches.push(equals('kind', kind));

  if (prefix) {
    // The ClickHouse-backed search rejects `or` groups, so the multi-field
    // prefix list below can't be expressed; `q` replaces it server-side,
    // prefix-matching key, name, firstName, lastName, and email.
    if (enableClickhouseContextsApiChangesInTheUi()) {
      matches.push(equals('q', prefix));
    } else {
      let orList = [startsWith('*./name', prefix), startsWith('key', prefix)];
      if (kind === ContextKindKey.USER) {
        orList = [
          ...orList,
          startsWith('user.lastName', prefix),
          startsWith('user.firstName', prefix),
          startsWith('user.email', prefix),
        ];
      }
      matches.push(parenthesized(or(orList)));
    }
  }

  searchParams.append('filter', encodeURIComponent(queryfilterToString(and(matches))));
  searchParams.append('limit', '20');
  if (!enableClickhouseContextsApiChangesInTheUi()) {
    searchParams.append('sort', '-ts');
  }
  url.search = searchParams.toString();

  return http.post(url.toString()).then(middleware.json).catch(middleware.jsonError);
};

export const fetchContextAttributeValues = (
  projectKey: string,
  environmentKey: string,
  kind: string,
  attributeKey: string,
  prefix?: string,
) => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/context-attributes/${encodeURIComponent(
      attributeKey,
    )}`,
    window.location.origin,
  );
  const searchParams = new URLSearchParams();
  const matches: QueryFilter[] = [];
  matches.push(equals('kind', kind));
  if (prefix) {
    matches.push(startsWith('value', prefix));
  }
  searchParams.append('filter', encodeURIComponent(queryfilterToString(and(matches))));
  searchParams.append('limit', '100');
  url.search = searchParams.toString();
  return http.get(url.toString()).then(middleware.json).catch(middleware.jsonError);
};

export const resolveContexts = (
  projectKey: string,
  environmentKey: string,
  contextTargetKindKeysOrEmails: ContextTargetKindKey[],
  sort?: string,
  searchEmails: boolean = false,
) => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/contexts/search`,
    window.location.origin,
  );
  const matches: QueryFilter[] = [];
  const searchParams = new URLSearchParams();
  if (!contextTargetKindKeysOrEmails.length) {
    return Promise.resolve([]);
  }

  const hasAllUserKindKeys = contextTargetKindKeysOrEmails.every((el) => el.kind === ContextKindKey.USER);

  const kindKeyFilter = anyOf('kindkey', contextTargetKindKeysOrEmails.map(makeKindKeyStringFromKindKey));

  if (searchEmails && hasAllUserKindKeys) {
    const emailFilters = contextTargetKindKeysOrEmails.map((el) => equals('user.email', el.key));
    matches.push(or([kindKeyFilter, ...emailFilters]));
  } else {
    matches.push(kindKeyFilter);
  }

  const filter = queryfilterToString(and(matches));
  searchParams.append('filter', encodeURIComponent(filter));
  searchParams.append('limit', contextTargetKindKeysOrEmails.length.toString());
  const resolvedSort = contextsSearchSort(sort || '-ts');
  if (resolvedSort !== undefined) {
    searchParams.append('sort', resolvedSort);
  }
  url.search = searchParams.toString();

  return http
    .post(url.toString())
    .then(middleware.json)
    .then((data) => augmentResolveContextsResponse(data))
    .catch(middleware.jsonError);
};

export type ContextKindRequest = schemas['UpsertContextKindPayload'];

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(() => resolve(), ms));
export const updateContextKindKey = async (
  projectKey: string,
  contextKindKey: string,
  contextKindRequest: ContextKindRequest,
) => {
  const updateResponse = await http
    .put(`/api/v2/projects/${projectKey}/context-kinds/${contextKindKey}`, {
      body: JSON.stringify(contextKindRequest),
      beta: true,
    })
    .then(middleware.json)
    .catch(middleware.jsonError);

  let waiting = true;

  const pollingTimeout = setTimeout(() => {
    waiting = false;
  }, 60_000);

  // Despite the PUT returning a success, the next GET returns stale data
  // This loop ensures that the promise resolves only after we get a confirmation of the write
  // from the API. It will timeout and assume success after 1 minute to avoid hammering the API.
  while (waiting) {
    const verifyApiResponse: ContextKindsCollectionRep = await http
      .get(`/api/v2/projects/${projectKey}/context-kinds`)
      .then((r) => r.json());

    const matchingContextKind = verifyApiResponse.items.find((item) => item.key === contextKindKey);
    if (!matchingContextKind) {
      await wait(1000);
      continue;
    }
    if (contextKindRequest.archived !== undefined) {
      // matchingContextKind?.archived comes back as `undefined` if false
      if (contextKindRequest.archived !== !!matchingContextKind?.archived) {
        await wait(1000);
        continue;
      }
    }

    if (contextKindRequest.description !== undefined) {
      if (contextKindRequest.description !== matchingContextKind?.description) {
        await wait(1000);
        continue;
      }
    }

    if (contextKindRequest.name !== undefined) {
      if (contextKindRequest.name !== matchingContextKind?.name) {
        await wait(1000);
        continue;
      }
    }
    if (contextKindRequest.version !== undefined) {
      if (contextKindRequest.version !== matchingContextKind?.version) {
        await wait(1000);
        continue;
      }
    }

    // All checks passed
    break;
  }

  clearTimeout(pollingTimeout);

  return updateResponse;
};
/** Matches RESOLVE_TARGETS_MAX_LIMIT, the largest batch the contexts epic buffers. */
const MAX_NAMES_BACKFILL_BATCH = 50;

export const fetchContextsWithNames = (projectKey: string, environmentKey: string, missingNamesKindKeys: string[]) => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/contexts/search`,
    window.location.origin,
  );

  const matches: QueryFilter[] = [];
  matches.push(anyOf('kindkey', missingNamesKindKeys));
  // `exists` is what makes this a different question from the initial lookup: it can
  // resolve an older record of the context that still carries a name.
  matches.push(exists('*.name', true));

  const filter = queryfilterToString(and(matches));

  const searchParams = new URLSearchParams();
  searchParams.set('includeTotalCount', String(true));
  url.search = searchParams.toString();

  return http
    .post(url.toString(), {
      body: {
        filter,
        sort: contextsSearchSort('-ts'),
        // Callers batch up to 50 kind:keys but the ClickHouse-backed API's
        // default page size is 20, so the batch size must be sent to avoid
        // dropping results.
        limit: batchedContextsSearchLimit(MAX_NAMES_BACKFILL_BATCH),
      },
    })
    .then(middleware.json)
    .catch(middleware.jsonError);
};
